import { formatDateRange } from '@longy/common'
import { Skeleton } from '@longy/web-components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Calendar, ChevronRight, MapPin } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EarlyBirdPrice } from '../../components/events/EarlyBirdPrice'
import { EventImage } from '../../components/events/EventImage'
import { FullyBookedBadge } from '../../components/events/FullyBookedBadge'
import { MonthCalendar } from '../../components/events/MonthCalendar'
import { WebinarBadge } from '../../components/events/WebinarBadge'
import { Footer, Header } from '../../components/landing'
import { api, ClinicRouterOutput } from '../../core/trpc'
import { useLocale } from '../../hooks/useLocale'
import { priceFormatterFor } from '../../utils/formatPrice'
import { buildOgMeta } from '../../utils/og'

type MonthEvent = ClinicRouterOutput['event']['getEventsByMonth'][number]
type PublicEvent = ClinicRouterOutput['event']['getPublicEvents'][number]

const EventCard = ({ event }: { event: MonthEvent }) => {
    const { t } = useTranslation()
    const { getLocalizedValue } = useLocale()

    return (
        <div className="group flex flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl">
            <div className="relative">
                <EventImage
                    alt={event.name ? getLocalizedValue(event.name) : 'Esemény'}
                    className="aspect-[4/3]"
                    fileId={event.images?.[0]?.file?.id}
                />
                <div className="absolute right-4 top-4 flex flex-wrap justify-end gap-2">
                    <WebinarBadge type={event.type} />
                    <FullyBookedBadge fullyBooked={event.fullyBooked} />
                </div>
                {event.categoryName && (
                    <div className="absolute left-4 top-4 rounded-full bg-brand-yellow px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-brand-dark shadow-sm">
                        {getLocalizedValue(event.categoryName)}
                    </div>
                )}
            </div>
            <div className="flex flex-1 flex-col p-8">
                <h3 className="mb-4 text-xl font-bold text-brand-dark">
                    {event.name ? getLocalizedValue(event.name) : 'Esemény'}
                </h3>
                <div className="mb-8 space-y-2 text-sm leading-relaxed text-brand-gray">
                    {event.startDate && (
                        <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-primary" />
                            {formatDateRange(event.startDate, event.endDate)}
                        </div>
                    )}
                    {event.locationName && getLocalizedValue(event.locationName) && (
                        <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-primary" />
                            {getLocalizedValue(event.locationName)}
                        </div>
                    )}
                </div>
                <div className="mt-auto flex items-end justify-between gap-4">
                    {event.minPrice !== null ? (
                        <EarlyBirdPrice
                            showLabel
                            format={priceFormatterFor(event.tax, (price) =>
                                t('app.event.netPlusVat', { price }),
                            )}
                            originalPrice={event.minOriginalPrice}
                            price={event.minPrice}
                            priceClassName="text-sm font-bold text-primary"
                            showDeadline={false}
                            suffix={event.hasMultiplePrices ? '-tól' : undefined}
                        />
                    ) : (
                        <span />
                    )}
                    <Link
                        className="flex items-center gap-1 font-bold text-primary transition-colors hover:text-brand-yellow"
                        params={{ eventId: event.id }}
                        to="/events/$eventId"
                    >
                        Részletek <ChevronRight className="h-4 w-4" />
                    </Link>
                </div>
            </div>
        </div>
    )
}

const NextUpcomingEvent = ({ event }: { event: PublicEvent }) => {
    const { getLocalizedValue } = useLocale()

    return (
        <Link
            className="group mt-8 flex overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
            params={{ eventId: event.id }}
            to="/events/$eventId"
        >
            <EventImage
                alt={event.name ? getLocalizedValue(event.name) : 'Esemény'}
                className="aspect-[4/3] w-32 flex-shrink-0 self-center sm:w-48"
                fileId={event.images?.[0]?.file?.id}
            />
            <div className="flex flex-1 flex-col justify-center p-6">
                <div className="mb-2 flex flex-wrap gap-2">
                    <WebinarBadge type={event.type} />
                    <FullyBookedBadge fullyBooked={event.fullyBooked} />
                </div>
                <h3 className="mb-2 text-lg font-bold text-brand-dark">
                    {event.name ? getLocalizedValue(event.name) : 'Esemény'}
                </h3>
                <div className="space-y-1 text-sm text-brand-gray">
                    {event.startDate && (
                        <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-primary" />
                            {formatDateRange(event.startDate, event.endDate)}
                        </div>
                    )}
                    {event.locationName && getLocalizedValue(event.locationName) && (
                        <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-primary" />
                            {getLocalizedValue(event.locationName)}
                        </div>
                    )}
                </div>
                <div className="mt-3 flex items-center gap-1 font-bold text-primary">
                    Részletek <ChevronRight className="h-4 w-4" />
                </div>
            </div>
        </Link>
    )
}

const EventsPage = () => {
    const { t } = useTranslation()
    const now = new Date()
    const [displayYear, setDisplayYear] = useState(now.getFullYear())
    const [selectedYear, setSelectedYear] = useState(now.getFullYear())
    const [selectedMonth, setSelectedMonth] = useState<number | null>(now.getMonth())
    const isAllEventsMode = selectedMonth === null

    const { data: monthEvents, isLoading: isMonthLoading } = api.event.getEventsByMonth.useQuery(
        { year: selectedYear, month: selectedMonth ?? 0 },
        { enabled: !isAllEventsMode },
    )
    const { data: upcomingEvents, isLoading: isUpcomingLoading } =
        api.event.getUpcomingEvents.useQuery(undefined, { enabled: isAllEventsMode })
    const { data: monthlyCounts } = api.event.getMonthlyEventCounts.useQuery({ year: displayYear })

    const events = isAllEventsMode ? upcomingEvents : monthEvents
    const isLoading = isAllEventsMode ? isUpcomingLoading : isMonthLoading

    const hasNoEvents = !isLoading && (!events || events.length === 0)
    const { data: publicEvents } = api.event.getPublicEvents.useQuery(undefined, {
        enabled: hasNoEvents && !isAllEventsMode,
    })
    const nextUpcomingEvent = publicEvents?.[0]

    const handleSelectMonth = (year: number, month: number) => {
        setSelectedYear(year)
        setSelectedMonth(month)
    }

    const MONTH_NAMES = [
        'január',
        'február',
        'március',
        'április',
        'május',
        'június',
        'július',
        'augusztus',
        'szeptember',
        'október',
        'november',
        'december',
    ]

    return (
        <div className="min-h-screen bg-off-white font-sans text-brand-dark">
            <Header />
            <main className="flex flex-col">
                <section className="bg-white py-24">
                    <div className="mx-auto w-full max-w-[1280px] px-6">
                        <div className="mb-12">
                            <h1 className="mb-4 text-3xl font-bold text-brand-dark md:text-4xl">
                                Események
                            </h1>
                            <p className="max-w-xl text-brand-gray">
                                Válasszon hónapot a naptárból az események megtekintéséhez.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[320px_1fr]">
                            <div>
                                <MonthCalendar
                                    displayYear={displayYear}
                                    monthlyCounts={monthlyCounts}
                                    selectedYear={selectedYear}
                                    selectedMonth={selectedMonth}
                                    onChangeDisplayYear={setDisplayYear}
                                    onSelectMonth={handleSelectMonth}
                                />
                                <button
                                    className={`mt-4 w-full rounded-2xl border px-4 py-3 text-sm font-bold transition-colors ${
                                        isAllEventsMode
                                            ? 'border-primary bg-primary text-white shadow-sm'
                                            : 'border-slate-100 bg-white text-brand-dark shadow-sm hover:bg-slate-50'
                                    }`}
                                    onClick={() => setSelectedMonth(null)}
                                >
                                    {t('frontend.events.showAllEvents')}
                                </button>
                            </div>

                            <div>
                                <h2 className="mb-8 text-2xl font-bold text-brand-dark">
                                    {selectedMonth === null
                                        ? t('frontend.events.allEvents')
                                        : `${selectedYear}. ${MONTH_NAMES[selectedMonth]}`}
                                </h2>

                                {isLoading ? (
                                    <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
                                        {[1, 2, 3, 4].map((i) => (
                                            <div
                                                key={i}
                                                className="overflow-hidden rounded-3xl border border-slate-100"
                                            >
                                                <Skeleton className="h-72 w-full" />
                                                <div className="p-8">
                                                    <Skeleton className="mb-4 h-6 w-48" />
                                                    <div className="mb-8 space-y-2">
                                                        <Skeleton className="h-4 w-40" />
                                                        <Skeleton className="h-4 w-32" />
                                                    </div>
                                                    <Skeleton className="h-10 w-full rounded-full" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : events && events.length > 0 ? (
                                    <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
                                        {events.map((event) => (
                                            <EventCard key={event.id} event={event} />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="py-16 text-center">
                                        <p className="text-lg text-brand-gray">
                                            {isAllEventsMode
                                                ? t('frontend.events.noUpcomingEvents')
                                                : 'Ebben a hónapban nincsenek események.'}
                                        </p>
                                        {nextUpcomingEvent && (
                                            <div className="mt-8">
                                                <p className="text-lg font-semibold text-brand-dark">
                                                    Legközelebbi eseményünk
                                                </p>
                                                <NextUpcomingEvent event={nextUpcomingEvent} />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    )
}

export const Route = createFileRoute('/events/')({
    component: EventsPage,
    loader: async ({ context }) => {
        const [, websiteSettings] = await Promise.allSettled([
            context.apiUtils.webContent.getAllPublic.ensureData(),
            context.apiUtils.websiteSettings.getPublic.ensureData(),
        ])
        return {
            websiteSettings: websiteSettings.status === 'fulfilled' ? websiteSettings.value : null,
        }
    },
    head: ({ loaderData }) => ({
        meta: buildOgMeta({
            title: 'Long-Y - Életmód Táborok',
            url: `${import.meta.env.VITE_WEB_URL || ''}/events`,
            websiteSettings: loaderData?.websiteSettings,
        }),
    }),
})
